import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiFetch, apiFetchBlob } from "./api";
import type { TaperStep, TemplateMedicine } from "@emr/shared";
import type { DischargeHeading, DischargeTemplateSection } from "@emr/shared/constants";
import { normalizeWeeklySchedule } from "@emr/shared/scheduling";
import type { SlotDef } from "@emr/shared/scheduling";

export interface Kpis {
  todaysAppointments: number;
  checkedInNow: number;
  completedToday: number;
  cancelledNoShowToday: number;
  pendingInvoicesCount: number;
  pendingInvoicesTotalPaise: number;
  newPatientsThisMonth: number;
}
export interface TimelineRow {
  appointmentId: string;
  patientName: string | null;
  clinicUhid: string | null;
  doctorName: string | null;
  doctorId: string | null;
  slot: string | null;
  arrivalTime: string | null;
  status: "Pending" | "Booked" | "CheckedIn" | "Completed" | "Cancelled" | "NoShow";
  patientId: string | null;
  photoR2Key?: string | null;
}
export interface UnpaidInvoice {
  id: string; number: string; patientName: string | null; totalPaise: number; paidPaise: number; status: string; date: string;
}
export interface PendingLab { id: string; visitId: string | null; status: string; createdAt: string; }
export interface PendingReviewAppt { id: string; patientName: string | null; clinicUhid: string | null; createdAt: string; }
export interface DashboardStats {
  kpis: Kpis;
  timeline: TimelineRow[];
  pendingTasks: {
    unpaidInvoices: UnpaidInvoice[];
    pendingLabs: PendingLab[];
    noShowsToday: { id: string; patientName: string | null; clinicUhid: string | null }[];
    pendingReviewAppts: PendingReviewAppt[];
  };
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ["dashboard", "stats"],
    queryFn: () => apiFetch<DashboardStats>("/dashboard/stats"),
    refetchOnWindowFocus: true,
  });
}

export interface AppointmentRow {
  id: string;
  patientId: string | null;
  doctorId: string | null;
  slot: string | null;
  arrivalTime: string | null;
  checkInAt: string | null;
  queueOrder: number | null;
  /** Whole-day calling serial, assigned at check-in across ALL slots (Phase 2).
   * Null on legacy/pre-rollout rows and non-checked-in appointments. */
  daySerial: number | null;
  status: "Pending" | "Booked" | "CheckedIn" | "Completed" | "Cancelled" | "NoShow";
  reason: string | null;
  whatsappReminderEnabled: boolean | null;
  originVisitId: string | null;
  bookedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  patientName: string | null;
  clinicUhid: string | null;
  dob: string | null;
  gender: "Male" | "Female" | "Other" | null;
  phone: string | null;
  photoR2Key: string | null;
  doctorName: string | null;
  visitId: string | null;
  flags: { name: string; color: string; kind: "user" | "admin" }[];
  /** True when the patient has no visit from any earlier calendar day (first-timer). */
  isNewPatient: boolean | null;
}

export function useAppointments(date?: string, slot?: string, status?: string) {
  const qs = new URLSearchParams();
  if (date) qs.set("date", date);
  if (slot) qs.set("slot", slot);
  if (status) qs.set("status", status);
  const query = qs.toString();
  return useQuery({
    queryKey: ["appointments", date ?? "all", slot ?? "all", status ?? "all"],
    queryFn: () => apiFetch<{ rows: AppointmentRow[] }>(`/appointments${query ? `?${query}` : ""}`),
    enabled: date !== undefined || status !== undefined,
  });
}

export function useAppointmentsByStatus(status: "Pending" | "Booked" | "CheckedIn" | "Completed" | "Cancelled" | "NoShow") {
  return useQuery({
    queryKey: ["appointments", "status", status],
    queryFn: () => apiFetch<{ rows: AppointmentRow[] }>(`/appointments?status=${status}`),
  });
}

export interface Patient {
  id: string;
  clinicUhid: string;
  oldUhid: string | null;
  salutation: string | null;
  name: string;
  dob: string | null;
  gender: "Male" | "Female" | "Other" | null;
  phone: string | null;
  altPhone: string | null;
  email: string | null;
  address: string | null;
  state: string | null;
  pincode: string | null;
  aadhaarId: string | null;
  abhaId: string | null;
  photoR2Key: string | null;
  enrolledBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export function usePatient(id?: string) {
  return useQuery({
    queryKey: ["patients", id],
    queryFn: () => apiFetch<{ patient: Patient }>(`/patients/${id}`),
    enabled: !!id,
  });
}

// ---- Patient passport photo (R2-served JPEG, authed) ------------------------
// Stores the Blob in the query cache; the component owns the object-URL
// lifecycle (create in an effect, revoke on change/unmount — no leaks).
// Query key incorporates photoR2Key so replacing the photo refetches.
export function usePatientPhotoBlob(patientId?: string, photoR2Key?: string | null) {
  return useQuery({
    queryKey: ["patient-photo", patientId, photoR2Key],
    queryFn: () => apiFetchBlob(`/patients/${patientId}/photo`),
    enabled: !!patientId && !!photoR2Key,
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
  });
}

export function usePatients(q: string, page = 1, limit = 20) {
  const qs = new URLSearchParams({ q, page: String(page), limit: String(limit) });
  return useQuery({
    queryKey: ["patients", "list", q, page, limit],
    queryFn: () => apiFetch<{ rows: Patient[]; page: number; limit: number; total: number }>(`/patients?${qs}`),
    // Runs only for a real search (q non-empty) or once the user has paged
    // past page 1 (2026-10-31, Batch 4): callers now pass the RAW debounced
    // query — the old `q || " "` sentinel (a 1-char blank search minted only
    // to defeat this gate) is gone. Empty search + page 1 → no fetch (the
    // patients page shows its "Start by creating a patient" empty state,
    // which was the page's behavior before the sentinel).
    enabled: q.length > 0 || page > 1,
    // Short staleTime (30s): patient list is clinical — a just-added patient
    // must appear in the next search within seconds, not minutes. 30s lets
    // rapid back-navigations serve from cache without re-paying the RTT,
    // while keeping staleness bounded. (Catalogs use 5min; patients do not.)
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export interface SlotBlock {
  id: string;
  date: string;
  slot: string | null;
  maxOverride: number | null;
  startOverride: string | null;
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
}

export function useSlotBlocks(from?: string, to?: string) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const query = qs.toString();
  return useQuery({
    queryKey: ["slot-blocks", from ?? "all", to ?? "all"],
    queryFn: () => apiFetch<{ rows: SlotBlock[] }>(`/slot-blocks${query ? `?${query}` : ""}`),
  });
}

export interface SlotCapacity {
  date: string;
  Morning: { booked: number; pending: number; max: number };
  Evening: { booked: number; pending: number; max: number };
}

export function useSlotCapacity(date?: string) {
  return useQuery({
    queryKey: ["slot-capacity", date ?? "none"],
    queryFn: () => apiFetch<SlotCapacity>(`/appointments/capacity?date=${date}`),
    enabled: !!date,
  });
}

// ---------------- Effective slot config (Phase 3, 2026-10-18) ----------------
//
// GET /slot-configs/effective?date= returns the APPLIED config a date
// resolves to (authMiddleware-only — no perm needed; slot defs are non-secret
// operational data every appointments viewer needs). Shape:
//   { configId, effectiveFrom, latePolicy, slots: SlotDef[] } (sorted by sortOrder).

export type LatePolicy = "first" | "in_order" | "last";

export interface EffectiveSlotConfig {
  configId: string;
  effectiveFrom: string;
  latePolicy: LatePolicy;
  slots: SlotDef[];
}

// ---------------- Slot config versions (Phase 4, 2026-10-18) ----------------
//
// GET /slot-configs — every config (applied + drafts) with its slot defs
// (scheduling.manage-gated). Shape per row:
//   { id, effectiveFrom, latePolicy, appliedAt: string|null, slots: SlotDef[] }.

export interface SlotConfigVersion {
  id: string;
  effectiveFrom: string;
  latePolicy: LatePolicy;
  appliedAt: string | null;
  slots: SlotDef[];
}

export function useSlotConfigs(enabled = true) {
  return useQuery({
    queryKey: ["slot-configs"],
    queryFn: () => apiFetch<{ rows: SlotConfigVersion[] }>(`/slot-configs`),
    enabled,
  });
}

// Preview dry-run (GET /slot-configs/preview?configId=) — the wizard's step 1.
export interface SlotConfigPreviewRow {
  id: string;
  patientName: string;
  clinicUhid: string | null;
  date: string | null;
  arrivalTime: string | null;
  oldSlot: string | null;
  newSlot: string | null;
  needsAssignment: boolean;
}

export interface SlotConfigPreview {
  rows: SlotConfigPreviewRow[];
  counts: { total: number; mapped: number; needsAssignment: number };
}

export function useSlotConfigPreview(configId?: string) {
  return useQuery({
    queryKey: ["slot-configs", "preview", configId],
    queryFn: () => apiFetch<SlotConfigPreview>(`/slot-configs/preview?configId=${configId}`),
    enabled: !!configId,
  });
}

// Edit preview (POST /slot-configs/:id/edit-preview) — the pending-plan edit
// wizard's Step-1 preview against the PROPOSED windows (2026-10-18). Same
// rows/counts shape as GET /preview. Manual (mutation-style) call: the body
// is the live proposed edit, so it must not be cached as a query — the
// wizard calls it on demand via apiFetch.
export type SlotConfigEditPreview = SlotConfigPreview;

// Backup status poll (GET /slot-configs/:id/backup-status) — the wizard's
// step 2. Manual refetch only (the wizard polls on a timer).
export interface SlotConfigBackupStatus {
  ready: boolean;
  lastBackup: unknown;
  completedAt: string | null;
  reason: string | null;
}

// Revert preview (GET /slot-configs/:id/revert-preview) — the pending-plan
// Delete dialog. Dry-run of the DELETE revert: each affected appointment's
// slot under the PREVIOUS policy (resolveSlotConfig over the remaining
// configs), by arrival time IST. availableSlots (2026-10-18): the revert
// target's slot options for that row's date — powers the Delete dialog's
// assignment dropdowns for needsRebooking rows.
export interface SlotConfigRevertPreviewRow {
  id: string;
  patientName: string;
  clinicUhid: string | null;
  date: string | null;
  arrivalTime: string | null;
  currentSlot: string | null;
  revertSlot: string | null;
  availableSlots: { key: string; label: string }[];
  needsRebooking: boolean;
}

export interface SlotConfigRevertPreview {
  rows: SlotConfigRevertPreviewRow[];
  counts: { total: number; mapped: number; needsRebooking: number };
}

export function useSlotConfigRevertPreview(configId?: string) {
  return useQuery({
    queryKey: ["slot-configs", "revert-preview", configId],
    queryFn: () => apiFetch<SlotConfigRevertPreview>(`/slot-configs/${configId}/revert-preview`),
    enabled: !!configId,
  });
}

// The GET /appointments/capacity response (Phase 2): per-key slots map +
// config summary, with legacy morning/evening top-level fields kept for
// backward compatibility (consumed as fallback while the config endpoint
// is unavailable).
export interface SlotCapacityResponse extends SlotCapacity {
  slots?: Record<string, { booked: number; pending: number; max: number }>;
  config?: EffectiveSlotConfig;
}

/** Fallback slot defs from the legacy app_settings fields (used only when the
 * effective-config endpoint fails or returns no config — the queue pages and
 * dialogs must never crash without a config; the v1 keys match the seeded
 * config so rows still group correctly). */
export function legacyFallbackSlots(settings?: PublicAppSettings | AppSettings | null): SlotDef[] {
  return [
    {
      key: "Morning",
      label: "Morning",
      startTime: settings?.morningSlotStart ?? "09:00",
      endTime: settings?.morningSlotEnd ?? "13:00",
      checkinStart: settings?.morningSlotStart ?? "09:00",
      checkinEnd: settings?.morningSlotEnd ?? "13:00",
      maxAppts: settings?.maxMorningAppts ?? 30,
      sortOrder: 1,
    },
    {
      key: "Evening",
      label: "Evening",
      startTime: settings?.eveningSlotStart ?? "17:00",
      endTime: settings?.eveningSlotEnd ?? "21:00",
      checkinStart: settings?.eveningSlotStart ?? "17:00",
      checkinEnd: settings?.eveningSlotEnd ?? "21:00",
      maxAppts: settings?.maxEveningAppts ?? 30,
      sortOrder: 2,
    },
  ];
}

/**
 * The date's effective slot config, with graceful degradation: when the
 * endpoint errors (network/503) or returns no applied config (404
 * no_slot_config), slotList falls back to the legacy Morning/Evening defs
 * built from the public settings fields — the page never crashes and always
 * has slots to render. `config` stays null in that case (callers can tell
 * fallback from a real config via configId presence).
 */
export function useEffectiveSlotConfig(date?: string, legacySettings?: PublicAppSettings | AppSettings | null) {
  const query = useQuery({
    queryKey: ["slot-config-effective", date ?? "none"],
    queryFn: () => apiFetch<EffectiveSlotConfig>(`/slot-configs/effective?date=${date}`),
    enabled: !!date,
    // 5-min staleTime: a date's config changes only via the (rare, audited)
    // apply flow; per-navigation refetches are pure waste. Mutations that
    // change configs invalidate this key (Phase 4 wizard).
    staleTime: 5 * 60 * 1000,
    // Graceful degradation: a failed fetch is NOT an error state for callers
    // — the fallback slot list below covers rendering. Keep the error in the
    // query result for anyone who needs to distinguish.
    retry: 1,
  });

  const slotList = useMemo(() => {
    if (query.data?.slots?.length) return query.data.slots;
    return legacyFallbackSlots(legacySettings);
  }, [query.data, legacySettings]);

  return { ...query, config: query.data ?? null, slotList };
}

export interface Drug { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null; createdAt: string; updatedAt: string; }

// Catalog cache policy (2026-09-22 performance brainstorm): drugs and
// investigations are small reference catalogs that change rarely. A 5-minute
// staleTime means the first load pays the RTT once, then subsequent navigations
// and tab-focus events serve instantly from the React Query cache. Admin
// mutations (create/update/delete/bulk) already invalidate ["drugs"] /
// ["investigations"] / ["investigation-categories"] (see mutations.ts), so
// edits propagate immediately — the staleTime only suppresses redundant
// background refetches, not user-triggered invalidations.
const CATALOG_STALE_MS = 5 * 60 * 1000; // 5 min
const CATALOG_GC_MS = 30 * 60 * 1000;   // 30 min (kept warm in memory)

export function useDrugs(q?: string) {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  return useQuery({
    queryKey: ["drugs", q ?? "all"],
    queryFn: () => apiFetch<{ rows: Drug[] } | Drug[]>(`/drugs${qs}`),
    staleTime: CATALOG_STALE_MS,
    gcTime: CATALOG_GC_MS,
    refetchOnWindowFocus: false,
  });
}

export interface Service { id: string; name: string; defaultAmountPaise: number; category: string | null; }

export function useServices() {
  return useQuery({
    queryKey: ["services"],
    queryFn: () => apiFetch<{ rows: Service[] } | Service[]>(`/services`),
  });
}

export interface Flag { id: string; name: string; color: string | null; kind: "user" | "admin"; sortOrder: number | null; }

export function useFlags() {
  return useQuery({
    queryKey: ["flags"],
    queryFn: () => apiFetch<{ rows: Flag[] } | Flag[]>(`/flags`),
  });
}

export interface ClinicDoctor { id: string; userId: string | null; name: string; qualifications: string | null; regNumber: string | null; specialty: string | null; consultationFeePaise: number | null; }

export function useClinicDoctors() {
  return useQuery({
    queryKey: ["clinic-doctors"],
    queryFn: () => apiFetch<{ rows: ClinicDoctor[] } | ClinicDoctor[]>(`/clinic-doctors`),
    staleTime: CATALOG_STALE_MS,
    gcTime: CATALOG_GC_MS,
    refetchOnWindowFocus: false,
  });
}

export interface AppSettings {
  id: string;
  clinicName: string | null;
  tagline: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  state: string | null;
  pincode: string | null;
  aiEndpoint: string;
  aiApiKey: string | null;
  aiModel: string;
  // Agentic flows (0030, 2026-10-18): separate model + temperature for the
  // scheduling assistant + apply-wizard AI; null model = fall back to
  // aiModel (0031 dropped the intermediate aiChatModel hop); null
  // temperature = model default (no param).
  aiAgenticModel: string | null;
  aiAgenticTemperature: number | null;
  aiTemperature: number | null;
  aiReasoningEffort: string | null;
  aiTranscribeModel: string;
  aiTranscribeStyle: string;
  aiTranscribeFormat: string;
  aiPromptReferralTo: string | null;
  aiPromptReferralReply: string | null;
  aiPromptCertificate: string | null;
  referralReplyWebhookUrl: string | null;
  referralReplyWebhookEnabled: boolean;
  referralReplyWebhookType: "pabbly" | "generic";
  referralReplyTemplate: string | null;
  referralToWebhookUrl: string | null;
  referralToWebhookEnabled: boolean;
  referralToWebhookType: "pabbly" | "generic";
  referralToTemplate: string | null;
  certificateWebhookUrl: string | null;
  certificateWebhookEnabled: boolean;
  certificateWebhookType: "pabbly" | "generic";
  certificateTemplate: string | null;
  whatsappReminderWebhookUrl: string | null;
  whatsappReminderWebhookEnabled: boolean;
  whatsappReminderWebhookType: "pabbly" | "generic";
  whatsappTemplate: string | null;
  whatsappEnabled: boolean;
  morningSlotStart: string;
  eveningSlotStart: string;
  morningSlotEnd: string;
  eveningSlotEnd: string;
  maxMorningAppts: number;
  maxEveningAppts: number;
  weeklySchedule: Record<string, { off: boolean; morningStart: string; eveningStart: string }>;
  uhidNextSerial: number;
  reminderLeadMinutes: number;
  r2Enabled: boolean;
  r2AccountId: string | null;
  r2BucketName: string | null;
  r2AccessKey: string | null;
  r2SecretKey: string | null;
  r2ApiToken: string | null;
  r2S3Endpoint: string | null;
  updatedAt: string;
}

export function useAppSettings() {
  return useQuery({
    queryKey: ["app-settings"],
    queryFn: async () => {
      const res = await apiFetch<{ settings: AppSettings }>(`/settings`);
      // weekly_schedule has historically been double-encoded in the DB
      // (jsonb string of JSON text — see post-mortem 2026-10-05 slot-block
      // fix). Normalize once at the read boundary so every consumer gets a
      // real 7-day object; normalizeWeeklySchedule also fills missing days
      // from DEFAULT_WEEKLY_SCHEDULE.
      return { ...res.settings, weeklySchedule: normalizeWeeklySchedule(res.settings.weeklySchedule) };
    },
  });
}

// ---------------- Public settings (authed-only, non-secret subset) ----------------
//
// Backed by GET /settings/public (authMiddleware-only, NO settings.view —
// added 2026-10-15 after the error-log audit found 512 403 rows/day from one
// front-desk user: Sidebar + Topbar (mounted for EVERY user) and the
// booking/EMR dialogs consumed the settings.view-gated GET /settings).
// Returns ONLY the clinic profile + scheduling fields + whatsappEnabled —
// no secrets, webhook URLs, AI prompts, or R2 config. Consumers that need
// those (Settings page, referral/certificate editors) keep useAppSettings().

export interface PublicAppSettings {
  clinicName: string | null;
  tagline: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  state: string | null;
  pincode: string | null;
  morningSlotStart: string;
  eveningSlotStart: string;
  morningSlotEnd: string;
  eveningSlotEnd: string;
  maxMorningAppts: number;
  maxEveningAppts: number;
  weeklySchedule: Record<string, { off: boolean; morningStart: string; eveningStart: string }>;
  reminderLeadMinutes: number;
  whatsappEnabled: boolean;
}

export function usePublicAppSettings() {
  return useQuery({
    queryKey: ["app-settings-public"],
    queryFn: async () => {
      const res = await apiFetch<{ settings: PublicAppSettings }>(`/settings/public`);
      // Same double-encoded weekly_schedule normalization as useAppSettings.
      return { ...res.settings, weeklySchedule: normalizeWeeklySchedule(res.settings.weeklySchedule) };
    },
    // 5-min staleTime: this data changes rarely (clinic profile + schedule)
    // and is read on every page mount via Sidebar/Topbar — a 30s default
    // would re-fetch it on every navigation. Settings-page edits still
    // propagate: useUpdateAppSettings invalidates ["app-settings"] AND
    // ["app-settings-public"].
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------- AI models (live from OpenRouter) ----------------

export interface AiModel {
  id: string;
  name: string;
  description: string;
  contextLength: number;
  supportedParameters: string[];
  pricing: { prompt: string; completion: string };
  reasoning?: {
    mandatory: boolean;
    defaultEnabled?: boolean;
    supportedEfforts?: string[];
    defaultEffort?: string;
  };
}

export interface AiModelsResponse {
  text: AiModel[];
  transcription: AiModel[];
  message?: string;
}

export function useAiModels(enabled = true) {
  return useQuery({
    queryKey: ["ai-models"],
    queryFn: () => apiFetch<AiModelsResponse>(`/ai/models`),
    // staleTime: 0 → refetch every time the page mounts/refreshes.
    // The model list on OpenRouter changes frequently (new models added
    // weekly), and the user wants to see all current models every time
    // they open Settings. TanStack Query's default refetchOnMount: true
    // + staleTime: 0 guarantees a fresh fetch on every page open.
    staleTime: 0,
    // enabled (2026-10-11): the dictation button's right-click model menu
    // reuses this query but must NOT fetch on page load for every user —
    // only when the menu opens (or the Settings page mounts, enabled=true).
    enabled,
  });
}

// Dictation upload format (0020) — read by the mic button so it encodes the
// recording into the user-selected format. The backing endpoint
// GET /ai/transcribe-config is authMiddleware-only (no requirePerm) because
// the button renders for every user with ai.use, while GET /settings is
// settings.view-gated. 5-min staleTime: the format changes rarely and the
// button only needs it at record start.
export interface TranscribeConfig {
  format: "wav" | "flac" | "mp3" | "aac" | "webm";
}

export function useTranscribeConfig(enabled = true) {
  return useQuery({
    queryKey: ["ai-transcribe-config"],
    queryFn: () => apiFetch<TranscribeConfig>(`/ai/transcribe-config`),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Only users who can see the dictation button (ai.use) need the format.
    enabled,
  });
}

// Transcription model health (2026-10-11) — probes the SAVED STT model with a
// 1s silence WAV (20s cap server-side) and returns { ok, model, message? }.
// Drives the dictation button's green-tick / red-X badge. The backing route
// GET /ai/transcribe-status is authMiddleware-only (no requirePerm) — every
// ai.use user sees the badge. ALWAYS 200, so the query only errors on network
// failures; ok:false is a normal result, not an error state.
export interface TranscribeStatus {
  ok: boolean;
  model: string;
  message?: string;
}

export function useTranscribeStatus(enabled = true) {
  return useQuery({
    queryKey: ["ai-transcribe-status"],
    queryFn: () => apiFetch<TranscribeStatus>(`/ai/transcribe-status`),
    // staleTime 0 + refetchOnMount: probe on every app load (the button is
    // mounted once per session in AppLayout). The upstream provider's state
    // (up/down) changes on OpenRouter's side, not ours — every fresh mount
    // deserves a fresh probe.
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false, // a failed probe IS the answer (down); don't hammer it
    enabled,
  });
}

export interface PatientFlagRow { id: string; flagId: string; patientId: string; addedByUserId: string; addedAt: string; name?: string; color?: string | null; }

export function usePatientFlags(patientId?: string) {
  return useQuery({
    queryKey: ["patients", patientId, "flags"],
    queryFn: () => apiFetch<{ rows: PatientFlagRow[] }>(`/patients/${patientId}/flags`),
    enabled: !!patientId,
  });
}

// ---------------- EMR Visit ----------------

export interface VisitDetail {
  id: string;
  appointmentId: string | null;
  patientId: string;
  doctorId: string | null;
  checkInAt: string;
  checkoutAt: string | null;
  reviewDate: string | null;
  reviewNeeded: boolean;
  patientName: string | null;
  patientDob: string | null;
  patientGender: "Male" | "Female" | "Other" | null;
  patientPhone: string | null;
  patientPhotoR2Key: string | null;
  clinicUhid: string | null;
  doctorName: string | null;
  doctorQualifications: string | null;
  doctorRegNumber: string | null;
}

export interface Icd10Code { code: string; description: string; }

export interface EmrNote {
  id: string;
  visitId: string;
  subjective: string | null;
  objective: string | null;
  diagnosis: string | null;
  plan: string | null;
  note: string | null;
  icd10Codes: Icd10Code[] | null;
  updatedAt: string;
}

export interface VitalRow {
  id: string;
  visitId: string;
  tempC: number | null;
  bpSystolic: number | null;
  bpDiastolic: number | null;
  pulse: number | null;
  respRate: number | null;
  spO2: number | null;
  heightCm: number | null;
  weightKg: number | null;
  bmi: number | null;
  recordedByUserId: string | null;
  recordedAt: string;
}

export interface MedicineRow {
  id: string;
  prescriptionId: string;
  drugId: string | null;
  serialNo: number;
  name: string;
  composition: string | null;
  formulation: string | null;
  strength: string | null;
  frequency: string | null;
  taper: TaperStep[] | null;
  duration: string | null;
  instructions: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PrescriptionRow {
  id: string;
  visitId: string;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  medicines: MedicineRow[];
}

export interface LabTestRow {
  id: string;
  labOrderId: string;
  testName: string;
  notes: string | null;
  status: "Ordered" | "Sampled" | "Resulted" | "Cancelled";
  result: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LabOrderRow {
  id: string;
  visitId: string;
  status: "Pending" | "Completed" | "Cancelled";
  createdAt: string;
  updatedAt: string;
  tests: LabTestRow[];
}

/**
 * Per-investigation metadata for Radiology / Electrophysiology tests.
 * Keyed by investigation ID. Only tests with data are stored; absent keys
 * or null values mean "no data for that investigation" — the row is
 * omitted from the print table. Lab investigations never use this map.
 */
export interface InvestigationDetail {
  regionAndSide: string | null;
  clinicalNotes: string | null;
}
export type InvestigationDetailsMap = Record<string, InvestigationDetail>;

export interface InvestigationRequestRow {
  id: string;
  patientId: string;
  visitId: string | null;
  createdByDoctorId: string | null;
  selectedInvestigationIds: string[];
  diagnosis: string | null;
  notes: string | null;
  radiologyNotes: string | null;
  electrophysiologyNotes: string | null;
  investigationDetails: InvestigationDetailsMap | null;
  createdAt: string;
  updatedAt: string;
}

export interface VisitAggregate {
  visit: VisitDetail;
  note: EmrNote | null;
  vitals: VitalRow[];
  prescriptions: PrescriptionRow[];
  labOrders: LabOrderRow[];
  investigationRequest: InvestigationRequestRow | null;
}

export function useVisit(id?: string) {
  return useQuery({
    queryKey: ["visits", id],
    queryFn: () => apiFetch<VisitAggregate>(`/visits/${id}`),
    enabled: !!id,
  });
}

export interface VisitListItem {
  id: string;
  appointmentId: string | null;
  patientId: string;
  doctorId: string | null;
  checkInAt: string;
  checkoutAt: string | null;
  reviewDate: string | null;
  reviewNeeded: boolean;
  doctorName: string | null;
  diagnosis: string | null;
}

export function usePatientVisits(patientId?: string) {
  return useQuery({
    queryKey: ["patients", patientId, "visits"],
    queryFn: () => apiFetch<{ rows: VisitListItem[] }>(`/visits?patientId=${patientId}`),
    enabled: !!patientId,
  });
}

export function useVisitNotes(visitId?: string) {
  return useQuery({
    queryKey: ["visits", visitId, "notes"],
    queryFn: () => apiFetch<{ note: EmrNote | null }>(`/visits/${visitId}/notes`),
    enabled: !!visitId,
  });
}

export function useVisitPrescriptions(visitId?: string) {
  return useQuery({
    queryKey: ["visits", visitId, "prescriptions"],
    queryFn: () => apiFetch<{ rows: PrescriptionRow[] }>(`/visits/${visitId}/prescriptions`),
    enabled: !!visitId,
  });
}

export function useVisitLabOrders(visitId?: string) {
  return useQuery({
    queryKey: ["visits", visitId, "lab-orders"],
    queryFn: () => apiFetch<{ rows: LabOrderRow[] }>(`/visits/${visitId}/lab-orders`),
    enabled: !!visitId,
  });
}

export function useVisitInvestigations(visitId?: string) {
  return useQuery({
    queryKey: ["visits", visitId, "investigations"],
    queryFn: () => apiFetch<{ investigationRequest: InvestigationRequestRow | null }>(`/visits/${visitId}/investigations`),
    enabled: !!visitId,
  });
}

// ---------------- Investigations catalog ----------------

export interface Investigation { id: string; name: string; category: string | null; }

export function useInvestigations() {
  return useQuery({
    queryKey: ["investigations"],
    queryFn: () => apiFetch<{ rows: Investigation[] }>(`/investigations`),
    staleTime: CATALOG_STALE_MS,
    gcTime: CATALOG_GC_MS,
    refetchOnWindowFocus: false,
  });
}

// ---------------- Invoices ----------------

export interface InvoiceListItem {
  id: string;
  number: string;
  patientId: string;
  visitId: string | null;
  date: string;
  subtotalPaise: number;
  totalPaise: number;
  paidPaise: number;
  status: "Unpaid" | "Partial" | "Paid";
  createdByDoctorId: string | null;
  createdAt: string;
  patientName: string | null;
  clinicUhid: string | null;
}

export function useInvoices(patientId?: string, visitId?: string) {
  const qs = new URLSearchParams();
  if (patientId) qs.set("patientId", patientId);
  if (visitId) qs.set("visitId", visitId);
  // Cap the unscoped/patient-wide list fetch (2026-10-31, Batch 4): the
  // billing page otherwise loads EVERY invoice; 50 covers a page of recent
  // rows (newest first, server-side default cap 100) without the unbounded
  // transfer. Scoped (patient/visit) lists are small already, but the cap is
  // harmless there too.
  qs.set("limit", "50");
  const query = qs.toString();
  return useQuery({
    queryKey: ["invoices", patientId ?? "all", visitId ?? "all"],
    queryFn: () => apiFetch<{ rows: InvoiceListItem[] }>(`/invoices${query ? `?${query}` : ""}`),
  });
}

export interface InvoiceDetail {
  id: string;
  number: string;
  patientId: string;
  visitId: string | null;
  date: string;
  lineItems: { serviceId?: string | null; name: string; quantity: number; amountPaise: number }[];
  subtotalPaise: number;
  totalPaise: number;
  paidPaise: number;
  status: "Unpaid" | "Partial" | "Paid";
  createdByDoctorId: string | null;
  createdAt: string;
  updatedAt: string;
  patientName: string | null;
  clinicUhid: string | null;
  patientPhone: string | null;
}

export function useInvoice(id?: string) {
  return useQuery({
    queryKey: ["invoices", id],
    queryFn: () => apiFetch<{ invoice: InvoiceDetail }>(`/invoices/${id}`),
    enabled: !!id,
  });
}

// ---------------- Documents ----------------

export interface DocumentRow {
  id: string;
  patientId: string | null;
  visitId: string | null;
  type: string | null;
  fileName: string;
  r2Key: string;
  mimeType: string | null;
  sizeBytes: number | null;
  description: string | null;
  documentDate: string;
  uploadedByUserId: string | null;
  uploadedAt: string;
  updatedAt: string;
}

export function useDocuments(patientId?: string, visitId?: string) {
  const qs = new URLSearchParams();
  if (visitId) qs.set("visitId", visitId);
  else if (patientId) qs.set("patientId", patientId);
  const query = qs.toString();
  return useQuery({
    queryKey: ["documents", patientId ?? "all", visitId ?? "all"],
    queryFn: () => apiFetch<{ rows: DocumentRow[] }>(`/documents${query ? `?${query}` : ""}`),
    enabled: !!patientId || !!visitId,
  });
}

// ---------------- Referring Doctors ----------------

export interface ReferringDoctor {
  id: string;
  name: string;
  gender: "Male" | "Female" | "Other";
  designation: string;
  clinicHospital: string;
  address: string;
  clinicPhone: string | null;
  personalPhone: string | null;
  email: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useReferringDoctors(q?: string) {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  return useQuery({
    queryKey: ["referring-doctors", q ?? "all"],
    queryFn: () => apiFetch<{ rows: ReferringDoctor[] } | ReferringDoctor[]>(`/referring-doctors${qs}`),
    staleTime: CATALOG_STALE_MS,
    gcTime: CATALOG_GC_MS,
    refetchOnWindowFocus: false,
  });
}

export function useReferringDoctor(id?: string) {
  return useQuery({
    queryKey: ["referring-doctors", id],
    queryFn: () => apiFetch<{ doctor: ReferringDoctor }>(`/referring-doctors/${id}`),
    enabled: !!id,
  });
}

// ---------------- Referral Letters ----------------

export interface ReferralLetterListItem {
  id: string;
  patientId: string;
  visitId: string | null;
  doctorId: string | null;
  createdByDoctorId: string | null;
  letterType: "Incoming" | "Outgoing";
  subject: string | null;
  body: string | null;
  aiContext: string | null;
  aiPolished: boolean;
  savedToR2: boolean;
  sentViaPabbly: boolean;
  r2Key: string | null;
  createdAt: string;
  updatedAt: string;
  patientName: string | null;
  clinicUhid: string | null;
  doctorName: string | null;
  doctorDesignation: string | null;
}

export function useReferralLetters(patientId?: string, visitId?: string) {
  const qs = new URLSearchParams();
  if (patientId) qs.set("patientId", patientId);
  if (visitId) qs.set("visitId", visitId);
  const query = qs.toString();
  return useQuery({
    queryKey: ["referrals", patientId ?? "all", visitId ?? "all"],
    queryFn: () => apiFetch<{ rows: ReferralLetterListItem[] }>(`/referrals${query ? `?${query}` : ""}`),
  });
}

export interface ReferralLetterDetail extends ReferralLetterListItem {
  patientPhone: string | null;
  patientDob: string | null;
  patientGender: "Male" | "Female" | "Other" | null;
  doctorGender: "Male" | "Female" | "Other" | null;
  doctorClinic: string | null;
  doctorAddress: string | null;
  signatoryName: string | null;
  signatoryQualifications: string | null;
  signatoryRegNumber: string | null;
}

export function useReferralLetter(id?: string) {
  return useQuery({
    queryKey: ["referrals", id],
    queryFn: () => apiFetch<{ letter: ReferralLetterDetail }>(`/referrals/${id}`),
    enabled: !!id,
  });
}

// ---------------- Medical Certificates ----------------

export interface MedicalCertificateListItem {
  id: string;
  patientId: string | null;
  visitId: string | null;
  createdByDoctorId: string | null;
  recipientType: "whomsoever" | "specific";
  recipientName: string | null;
  recipientSpecialization: string | null;
  recipientClinic: string | null;
  recipientAddress: string | null;
  certificateBody: string | null;
  aiContext: string | null;
  aiGenerated: boolean;
  savedToR2: boolean;
  sentViaPabbly: boolean;
  r2Key: string | null;
  createdAt: string;
  updatedAt: string;
  patientName: string | null;
  clinicUhid: string | null;
  signatoryName: string | null;
}

export function useMedicalCertificates(patientId?: string, visitId?: string) {
  const qs = new URLSearchParams();
  if (patientId) qs.set("patientId", patientId);
  if (visitId) qs.set("visitId", visitId);
  const query = qs.toString();
  return useQuery({
    queryKey: ["certificates", patientId ?? "all", visitId ?? "all"],
    queryFn: () => apiFetch<{ rows: MedicalCertificateListItem[] }>(`/certificates${query ? `?${query}` : ""}`),
  });
}

export interface MedicalCertificateDetail extends MedicalCertificateListItem {
  patientPhone: string | null;
  patientDob: string | null;
  patientGender: "Male" | "Female" | "Other" | null;
  signatoryQualifications: string | null;
  signatoryRegNumber: string | null;
}

export function useMedicalCertificate(id?: string) {
  return useQuery({
    queryKey: ["certificates", id],
    queryFn: () => apiFetch<{ certificate: MedicalCertificateDetail }>(`/certificates/${id}`),
    enabled: !!id,
  });
}

// ---------------- Investigations catalog (templates + categories) ----------------

export interface InvestigationTemplate { id: string; name: string; items: string[]; createdAt: string; }
export interface InvestigationCategory { id: string; name: string; createdAt: string; }

export function useInvestigationTemplates() {
  return useQuery({
    queryKey: ["investigation-templates"],
    queryFn: () => apiFetch<{ rows: InvestigationTemplate[] }>(`/investigations/templates`),
    staleTime: CATALOG_STALE_MS,
    gcTime: CATALOG_GC_MS,
    refetchOnWindowFocus: false,
  });
}

export function useInvestigationCategories() {
  return useQuery({
    queryKey: ["investigation-categories"],
    queryFn: () => apiFetch<{ rows: string[]; registered: string[] }>(`/investigations/categories`),
    staleTime: CATALOG_STALE_MS,
    gcTime: CATALOG_GC_MS,
    refetchOnWindowFocus: false,
  });
}

// ---------------- Patient Info Templates ----------------

export interface PatientInfoTemplate { id: string; title: string; content: string | null; createdAt: string; updatedAt: string; }

export function usePatientInfoTemplates() {
  return useQuery({
    queryKey: ["patient-info-templates"],
    queryFn: () => apiFetch<{ rows: PatientInfoTemplate[] }>(`/patient-info-templates`),
  });
}

// ---------------- Prescription Templates ----------------

// Shared TemplateMedicine (packages/shared/src/drugs.ts) — the medicines
// table's value columns minus id/prescription_id/serial_no. A template maps
// 1:1 onto prescription rows; the EMR page appends them as fresh MedicineRows.
export interface PrescriptionTemplate {
  id: string;
  name: string;
  medicines: TemplateMedicine[];
  // Optional group membership (migration 0036): null = "Ungrouped".
  groupId: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// Template group (migration 0036): named folder for the library above.
export interface PrescriptionTemplateGroup {
  id: string;
  name: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export function usePrescriptionTemplateGroups() {
  return useQuery({
    queryKey: ["prescription-template-groups"],
    queryFn: () => apiFetch<{ rows: PrescriptionTemplateGroup[] }>(`/prescription-template-groups`),
    staleTime: CATALOG_STALE_MS,
    gcTime: CATALOG_GC_MS,
    refetchOnWindowFocus: false,
  });
}

export function usePrescriptionTemplates() {
  return useQuery({
    queryKey: ["prescription-templates"],
    queryFn: () => apiFetch<{ rows: PrescriptionTemplate[] }>(`/prescription-templates`),
    staleTime: CATALOG_STALE_MS,
    gcTime: CATALOG_GC_MS,
    refetchOnWindowFocus: false,
  });
}

// ---------------- Admin Users ----------------

export interface AdminUserRow {
  // APPWRITE user id ($id) — the audit-trail / permissions / reset-password
  // routes resolve profiles by profiles.user_id = :id.
  id: string;
  // Postgres profiles.id UUID (2026-11-01, Batch 8 B8-1) — what
  // audit_log.changed_by / error_log.user_id store, so the audit/error-log
  // ?user= filter must use this, NOT `id`. Null when no profile row exists.
  profileId: string | null;
  email: string;
  name: string;
  role: "admin" | "user";
  permissionCount: number;
  permissionTotal: number;
  templateBadge: string | null;
  status: "Active" | "Inactive";
  createdAt: string;
}

export function useUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => apiFetch<{ users: AdminUserRow[] }>(`/admin/users`),
  });
}

export interface UserAuditTrailRow {
  id: string;
  tableName: string;
  recordId: string | null;
  action: string;
  changedAt: string;
  ip: string | null;
  userAgent: string | null;
}

export function useUserAuditTrail(id?: string) {
  return useQuery({
    queryKey: ["admin", "users", id, "audit-trail"],
    queryFn: () => apiFetch<{ rows: UserAuditTrailRow[] }>(`/admin/users/${id}/audit-trail`),
    enabled: !!id,
  });
}

export interface PermissionCatalogRow {
  id: string;
  key: string;
  module: string;
  action: string;
  label: string;
  description: string | null;
  sortOrder: number;
}

export function usePermissionsCatalog() {
  return useQuery({
    queryKey: ["permissions-catalog"],
    queryFn: () => apiFetch<{ permissions: PermissionCatalogRow[] }>(`/permissions`),
  });
}

export interface PermissionTemplateRow {
  id: string;
  name: string;
  description: string | null;
  permissionIds: string[];
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export function usePermissionTemplates() {
  return useQuery({
    queryKey: ["permission-templates"],
    queryFn: () => apiFetch<{ templates: PermissionTemplateRow[] }>(`/permissions/templates`),
  });
}

// ---------------- Audit Log ----------------

export interface AuditLogRow {
  id: string;
  tableName: string;
  recordId: string | null;
  action: string;
  oldData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
  changedBy: string | null;
  changedAt: string;
  ip: string | null;
  userAgent: string | null;
  changedByName: string | null;
}

export interface AuditLogResponse {
  rows: AuditLogRow[];
  page: number;
  limit: number;
  total: number;
}

export function useAuditLog(params: { user?: string; tableName?: string; from?: string; to?: string; page?: number; limit?: number }) {
  const qs = new URLSearchParams();
  if (params.user) qs.set("user", params.user);
  if (params.tableName) qs.set("tableName", params.tableName);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  qs.set("page", String(params.page ?? 1));
  qs.set("limit", String(params.limit ?? 50));
  return useQuery({
    queryKey: ["audit-log", params.user ?? "all", params.tableName ?? "all", params.from ?? "all", params.to ?? "all", params.page ?? 1, params.limit ?? 50],
    queryFn: () => apiFetch<AuditLogResponse>(`/audit-log?${qs.toString()}`),
  });
}

export function useAuditLogTables() {
  return useQuery({
    queryKey: ["audit-log", "tables"],
    queryFn: () => apiFetch<{ tables: string[] }>(`/audit-log/tables`),
  });
}

// ---------------- Error Log (admin-only) ----------------

export interface ErrorLogRow {
  id: string;
  code: string;
  status: number;
  method: string;
  path: string;
  message: string | null;
  userId: string | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  userName: string | null;
}

export interface ErrorLogResponse {
  rows: ErrorLogRow[];
  page: number;
  limit: number;
  total: number;
}

export function useErrorLog(params: { code?: string; status?: number; user?: string; from?: string; to?: string; page?: number; limit?: number }) {
  const qs = new URLSearchParams();
  if (params.code) qs.set("code", params.code);
  if (params.status) qs.set("status", String(params.status));
  if (params.user) qs.set("user", params.user);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  qs.set("page", String(params.page ?? 1));
  qs.set("limit", String(params.limit ?? 50));
  return useQuery({
    queryKey: ["error-log", params.code ?? "all", params.status ?? "all", params.user ?? "all", params.from ?? "all", params.to ?? "all", params.page ?? 1, params.limit ?? 50],
    queryFn: () => apiFetch<ErrorLogResponse>(`/error-log?${qs.toString()}`),
  });
}

export function useErrorLogCodes() {
  return useQuery({
    queryKey: ["error-log", "codes"],
    queryFn: () => apiFetch<{ codes: { code: string; count: number }[] }>(`/error-log/codes`),
  });
}

// ---------------- Reports ----------------

export interface ArchiveItem {
  id: string;
  patientId?: string | null;
  visitId?: string | null;
  patientName: string | null;
  clinicUhid: string | null;
  preview: string;
  savedToR2?: boolean;
  r2Key?: string | null;
  createdAt: string;
  [key: string]: unknown;
}

export interface ArchiveResponse {
  referralLetters: ArchiveItem[];
  medicalCertificates: ArchiveItem[];
  visits: ArchiveItem[];
  investigationRequests: ArchiveItem[];
  prescriptions: ArchiveItem[];
  invoices: ArchiveItem[];
}

export function useReportsArchive(params: { from?: string; to?: string; patientId?: string; type?: string }) {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.patientId) qs.set("patientId", params.patientId);
  if (params.type) qs.set("type", params.type);
  return useQuery({
    queryKey: ["reports", "archive", params.from ?? "all", params.to ?? "all", params.patientId ?? "all", params.type ?? "all"],
    queryFn: () => apiFetch<ArchiveResponse>(`/reports?${qs.toString()}`),
  });
}

export interface DailyCollectionRow {
  id: string;
  number: string;
  patientName: string | null;
  clinicUhid: string | null;
  date: string;
  createdAt: string;
  paidPaise: number;
  totalPaise: number;
  status: string;
  method?: string;
  time: string;
}

export interface DailyCollectionResponse {
  rows: DailyCollectionRow[];
  days: { day: string; totalPaise: number; count: number }[];
}

export function useDailyCollection(from?: string, to?: string) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  return useQuery({
    queryKey: ["reports", "daily-collection", from ?? "all", to ?? "all"],
    queryFn: () => apiFetch<DailyCollectionResponse>(`/reports/daily-collection?${qs.toString()}`),
    enabled: !!from && !!to,
  });
}

export interface NoShowRow {
  id: string;
  patientName: string | null;
  clinicUhid: string | null;
  phone: string | null;
  slot: string | null;
  arrivalTime: string;
  reason: string | null;
}

export function useNoShows(from?: string, to?: string) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  return useQuery({
    queryKey: ["reports", "no-shows", from ?? "all", to ?? "all"],
    queryFn: () => apiFetch<{ rows: NoShowRow[] }>(`/reports/no-shows?${qs.toString()}`),
    enabled: !!from && !!to,
  });
}

export interface UpcomingApptRow {
  id: string;
  patientName: string | null;
  clinicUhid: string | null;
  phone: string | null;
  slot: string | null;
  arrivalTime: string;
  status: string;
  doctorName: string | null;
  reason: string | null;
}

export function useUpcomingAppts(from?: string, to?: string) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  return useQuery({
    queryKey: ["reports", "upcoming", from ?? "all", to ?? "all"],
    queryFn: () => apiFetch<{ rows: UpcomingApptRow[] }>(`/reports/upcoming-appointments?${qs.toString()}`),
    enabled: !!from && !!to,
  });
}

// ---------------- Profiles (for audit log user filter) ----------------

export interface ProfileListRow {
  // Postgres profiles.id UUID — the id audit_log.changed_by / error_log.user_id
  // store. The audit/error-log ?user= filter must use this (B8-1 fix); Appwrite
  // $id never matches those columns (the old pre-B2 behavior was a 500, post-B2
  // a 422 invalid_user_param).
  profileId: string;
  fullName: string;
  role: "admin" | "user";
}

export function useProfilesList() {
  return useQuery({
    queryKey: ["profiles-list"],
    queryFn: async () => {
      const { users } = await apiFetch<{ users: AdminUserRow[] }>(`/admin/users`);
      // Only profile-backed users carry a filterable profiles.id UUID; a
      // profile-less Appwrite drift user can have no audit/error rows.
      return users.filter((u) => u.profileId != null).map((u) => ({ profileId: u.profileId!, fullName: u.name, role: u.role }));
    },
  });
}

// ---------------- Nav Pins (sidebar pinning, per-user) ----------------

export function useNavPins() {
  return useQuery({
    queryKey: ["nav-pins"],
    queryFn: async () => {
      const { routes } = await apiFetch<{ routes: string[] }>(`/nav-pins`);
      return new Set<string>(routes);
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

// ---------------- Dictation button position (per-user, DB-backed) -----------

// Floating dictation button x/y (migration 0021) — replaces the old
// localStorage key emr:dictation-btn-pos so the position follows the user
// across browsers/machines. x/y are null when the user has never dragged
// the button (the client falls back to its default bottom-right position).
export interface DictationPos {
  x: number | null;
  y: number | null;
}

export function useDictationPos(enabled = true) {
  return useQuery({
    queryKey: ["dictation-pos"],
    queryFn: () => apiFetch<DictationPos>(`/dictation-pos`),
    // 5-min staleTime: the position only changes via this client's own PUT,
    // so a background refetch is pure waste — but a refetch after 5 min
    // picks up a position saved from another machine (the whole point of
    // DB-backed storage).
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Only users who can see the dictation button (ai.use) need the position.
    enabled,
  });
}

// ---------------- Discharge Summaries (admin-only) ----------------

export interface DischargeSummaryListItem {
  id: string;
  patientId: string;
  visitId: string | null;
  heading: DischargeHeading;
  admissionAt: string | null;
  dischargeAt: string | null;
  diagnosis: string | null;
  reviewDate: string | null;
  savedToR2: boolean;
  r2Key: string | null;
  createdAt: string;
  updatedAt: string;
  patientName: string | null;
  clinicUhid: string | null;
  doctorName: string | null;
}

export function useDischargeSummaries(patientId?: string) {
  return useQuery({
    queryKey: ["discharge-summaries", patientId ?? "all"],
    queryFn: () =>
      apiFetch<{ rows: DischargeSummaryListItem[] }>(
        `/discharge-summaries${patientId ? `?patientId=${patientId}` : ""}`,
      ),
  });
}

export interface DischargeSummaryDetail extends DischargeSummaryListItem {
  history: string | null;
  examination: string | null;
  investigations: string | null;
  treatmentInHospital: string | null;
  courseInHospital: string | null;
  generalAdvice: string | null;
  showPrescription: boolean;
  showReviewDate: boolean;
  reviewText: string | null;
  doctorId: string | null;
  patientDob: string | null;
  patientGender: string | null;
  patientPhone: string | null;
  patientAddress: string | null;
  doctorQualifications: string | null;
  doctorRegNumber: string | null;
}

// The medicines payload of GET /discharge-summaries/:id — same shape as the
// visit MedicineRow minus prescriptionId (dischargeSummaryId instead).
export interface DischargeMedicineRow {
  id: string;
  dischargeSummaryId: string;
  drugId: string | null;
  serialNo: number;
  name: string;
  composition: string | null;
  formulation: string | null;
  strength: string | null;
  frequency: string | null;
  taper: TaperStep[] | null;
  duration: string | null;
  instructions: string | null;
}

export function useDischargeSummary(id?: string) {
  return useQuery({
    queryKey: ["discharge-summaries", id],
    queryFn: () =>
      apiFetch<{ summary: DischargeSummaryDetail; medicines: DischargeMedicineRow[] }>(
        `/discharge-summaries/${id}`,
      ),
    enabled: !!id,
  });
}

export interface DischargeSectionTemplateRow {
  id: string;
  section: DischargeTemplateSection;
  title: string;
  content: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useDischargeSectionTemplates() {
  return useQuery({
    queryKey: ["discharge-summaries", "templates"],
    queryFn: () => apiFetch<{ rows: DischargeSectionTemplateRow[] }>(`/discharge-summaries/templates`),
  });
}

// ---------------- Backups (admin-only) ----------------

// GET /backups — every backup in R2 (newest first), proxied from the
// emr-backup Worker. One row per (tier, date) pair; key is the dump object
// (the delete handle). dumpedAt/dumpBytes come from the manifest join.
export interface BackupRow {
  key: string;
  manifestKey: string;
  date: string;
  tier: string;
  sizeBytes: number;
  dumpedAt: string | null;
  dumpBytes: number | null;
}

export function useBackups(enabled = true) {
  return useQuery({
    queryKey: ["backups"],
    queryFn: () => apiFetch<{ backups: BackupRow[] }>(`/backups`),
    enabled,
    // The list changes only via the (rare) cron run or an explicit delete —
    // both of which invalidate this key — so a 5-min staleTime avoids
    // re-listing R2 on every navigation without hiding fresh deletes.
    staleTime: 5 * 60 * 1000,
    // A 502 (worker not redeployed yet) is the EXPECTED production state
    // until the pending deploy lands — don't hammer retries; the page
    // renders its graceful notice from the error.
    retry: 1,
  });
}

// ---------------- Block Days reschedule flow (2026-10-29) ----------------
// The Block Days flow's OWN preview query (NOT the slot-config wizard's):
// GET /block-reschedule/preview?date=&slot= — the appointments stranded on
// a block's (date, scope), mirroring POST /slot-blocks's must-move guard.
// Read-only; scheduling.manage-gated server-side. `slot: null` = whole day.

export interface BlockReschedulePreview {
  date: string;
  slot: string | null;
  scopeSlots: string[];
  config: { configId: string; effectiveFrom: string } | null;
  rows: {
    id: string;
    patientName: string;
    clinicUhid: string;
    status: string;
    slot: string | null;
    arrivalTime: string | null;
  }[];
}

export function useBlockReschedulePreview(date: string, slot: string | null, enabled: boolean) {
  const qs = new URLSearchParams({ date });
  if (slot) qs.set("slot", slot);
  return useQuery({
    queryKey: ["block-reschedule-preview", date, slot ?? "whole-day"],
    queryFn: () => apiFetch<BlockReschedulePreview>(`/block-reschedule/preview?${qs.toString()}`),
    enabled: enabled && !!date,
    // Fresh per open: the stranded set changes with every booking/cancel.
    staleTime: 0,
    retry: 1,
  });
}
